import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { Globe as WhiteLabelIcon } from "@phosphor-icons/react/Globe";
import { Plugs as PlugsIcon } from "@phosphor-icons/react/Plugs";
import { PlugsConnected as PlugsConnectedIcon } from "@phosphor-icons/react/PlugsConnected";
import { useState } from "react";

import { ConfirmationModal } from "~ui-components/index";
import { cleanDomain, isValidDomain } from "~utils/validation/domain";

// Fix: import Layout component correctly
import { BrandLayout } from "./layout";

export interface BrandDomainAndWhiteLabelProps {
	removeMangoBrandingChecked: boolean;
	useDomainChecked: boolean;
	customDomain: string;
	isConnected: boolean;
	isLoading: boolean;
	error: string | null;
	onRemoveMangoBrandingChange: (checked: boolean) => void;
	onUseDomainChange: (checked: boolean) => void;
	onCustomDomainChange: (domain: string) => void;
	onConnectDomain: () => void;
	onDeleteSubdomain: () => void;
	onErrorDismiss: () => void;
}

export const BrandDomainAndWhiteLabel = ({
	removeMangoBrandingChecked,
	useDomainChecked,
	customDomain,
	isConnected,
	isLoading,
	error,
	onRemoveMangoBrandingChange,
	onUseDomainChange,
	onCustomDomainChange,
	onConnectDomain,
	onDeleteSubdomain,
	onErrorDismiss,
}: BrandDomainAndWhiteLabelProps) => {
	const [openModal, setOpenModal] = useState(false);
	const [deleteModal, setDeleteModal] = useState(false);

	const handleCustomDomainChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const value = event.target.value;
		const cleanValue = cleanDomain(value);
		onCustomDomainChange(cleanValue);
	};

	const handleCustomDomainConnect = () => {
		if (isConnected) {
			setDeleteModal(true);
		} else {
			setOpenModal(true);
		}
	};

	const handleConfirmConnection = () => {
		onConnectDomain();
		setOpenModal(false);
	};

	const handleConfirmDelete = () => {
		onDeleteSubdomain();
		setDeleteModal(false);
	};

	const isDomainValid = customDomain.length > 0 && isValidDomain(customDomain);

	return (
		<BrandLayout pageTitle="Domain & whitelabel">
			{/* Connection Confirmation Modal */}
			<ConfirmationModal
				open={openModal}
				title="Connect Domain"
				content={`Are you sure you want to connect ${customDomain} to Mango? This will create a new subdomain record for your account.`}
				actionText="Connect"
				onClose={() => setOpenModal(false)}
				onConfirm={handleConfirmConnection}
			/>

			{/* Delete Confirmation Modal */}
			<ConfirmationModal
				open={deleteModal}
				title="Delete Subdomain"
				content={`Are you sure you want to delete the subdomain ${customDomain}? This action cannot be undone.`}
				actionText="Delete"
				onClose={() => setDeleteModal(false)}
				onConfirm={handleConfirmDelete}
			/>

			{/* Error Alert */}
			{error && (
				<Alert
					severity="error"
					onClose={onErrorDismiss}
					sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<Card>
				<CardHeader
					avatar={
						<Avatar>
							<WhiteLabelIcon fontSize="var(--Icon-fontSize)" />
						</Avatar>
					}
					title="Enhance trust and elevate your brand by displaying maps under your own web address with full white-labeling"
				/>
				<CardContent>
					<Stack spacing={3}>
						<FormControl>
							<FormControlLabel
								control={
									<Switch
										checked={removeMangoBrandingChecked}
										onChange={(e) =>
											onRemoveMangoBrandingChange(e.target.checked)
										}
									/>
								}
								label="Remove Mango branding"
							/>
						</FormControl>
						<FormControl>
							<FormControlLabel
								control={
									<Switch
										checked={useDomainChecked}
										onChange={(e) => onUseDomainChange(e.target.checked)}
									/>
								}
								label="Use your own domain"
							/>
						</FormControl>
						{useDomainChecked && (
							<>
								<Divider />
								{!isConnected && (
									<Typography
										variant="body2"
										color="text.primary">
										In your domain&apos;s DNS settings, add a new A-Record with
										the value 174.129.28.51
									</Typography>
								)}
								<Card>
									<CardContent
										component={Box}
										alignItems={"center"}
										alignContent={"center"}>
										<Stack
											direction="row"
											justifyContent={"space-between"}
											marginTop={1}>
											<Stack
												spacing={2}
												direction="row">
												{isConnected ? (
													<Button
														startIcon={<PlugsConnectedIcon />}
														variant="text"
														color="success">
														Domain connected
													</Button>
												) : (
													<Stack>
														<Typography
															variant="subtitle1"
															color="text.primary">
															Custom domain
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary">
															Don&apos;t include https:// or www
														</Typography>
													</Stack>
												)}

												<FormControl variant="outlined">
													<OutlinedInput
														name="custom-domain"
														placeholder="example.com"
														label="Custom domain"
														value={customDomain}
														onChange={handleCustomDomainChange}
														error={customDomain.length > 0 && !isDomainValid}
														disabled={isConnected}
													/>
												</FormControl>
											</Stack>
											<Button
												startIcon={<PlugsIcon />}
												variant="outlined"
												onClick={handleCustomDomainConnect}
												disabled={(!isDomainValid && !isConnected) || isLoading}
												color={isConnected ? "error" : "primary"}>
												{isConnected ? "Delete Subdomain" : "Connect to Mango"}
											</Button>
										</Stack>
									</CardContent>
								</Card>
							</>
						)}
					</Stack>
				</CardContent>
			</Card>
		</BrandLayout>
	);
};
